import React, { useState, useEffect, Fragment, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { makeStyles } from "@mui/styles";
import { useStores } from "../../../core/stores/index";
import "./MyProfile.css";
import { Button, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { IUser } from "common/interfaces/IUser";
//import Select from "react-select";
import { Options } from "react-select/dist/declarations/src";
import { useNavigate, useParams } from "react-router-dom";
import { ISaveUserRequest } from "common/interfaces/apiRequest/SaveUserRequest";
import { UserType } from "common/enums/UserType";
import { Role } from "common/enums/Role";
import { useSnackbar } from "notistack";
import { IUpdateUserRequest } from "common/interfaces/apiRequest/IUpdateUserRequest";
import { IDelegationUser } from "common/interfaces/IDelegationUser";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { DateHelper } from "common/helpers/DateHelper";
import { Spinner } from "components/common/Spinner";
import { UserStore } from "core/stores/UserStore";

interface MyProfileProp {
    children: JSX.Element;
}

export const MyProfile = observer((props: MyProfileProp) => {
    const { userStore } = useStores();
    const navigate = useNavigate();
    const snackbar = useSnackbar();

    const { userId } = useParams() as { userId: string };
    const [currentUser, setCurrentUser] = useState<IUser | undefined>(undefined);

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const getDateFormatted = (dateString: string | undefined) => {
        if (DateHelper.isValidDateOrString(dateString)) {
            return DateHelper.dateToInternationalString(new Date(dateString as string));
        } else {
            return undefined;
        }
    };

    const [isNameValid, setIsNameValid] = useState<boolean>(true);
    const [isUserTypeValid, setIsUserTypeValid] = useState<boolean>(true);
    const [isDelegationUserValid, setIsDelegationUserValid] = useState<boolean>(true);
    const [isDelegationEndDateValid, setIsDelegationEndDateValid] = useState<boolean>(true);

    const [userTypeSelection, setUserTypeSelection] = React.useState<UserType | string | undefined>(currentUser?.userType);

    const [userEnabled, setUserEnabled] = useState<boolean>(true);

    const [userName, setUserName] = useState<string | undefined>(currentUser?.name);

    const [isDelegationActive, setIsDelegationActive] = useState<boolean | undefined>(currentUser?.isDelegationActive);

    const [userRoles, setUserRoles] = useState<string[]>(currentUser?.roles ? (currentUser?.roles as string[]) : []);

    const [delegationEndDate, setDelegationEndDate] = useState<string | undefined>(getDateFormatted(currentUser?.delegationEndDate));

    const getSelectedDelegationUser = (delegationUserId: number): IDelegationUser | undefined => {
        if (userStore.users.length > 0) {
            const delegationUser = userStore.users.filter((x) => x.userId == delegationUserId)[0];
            return { name: delegationUser.name as string, userId: delegationUser.userId as number };
        }
    };

    const [selectedDelegationUser, setSelectedDelegationUser] = useState<IDelegationUser | undefined>(
        currentUser?.delegationUserId ? getSelectedDelegationUser(currentUser?.delegationUserId) : undefined
    );

    const onRolesCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setUserRoles([...userRoles, e.target.value]);
        } else {
            if (userRoles.includes(e.target.value)) {
                const alteredArray = userRoles.filter((x) => x != e.target.value);
                setUserRoles(alteredArray);
            }
        }
        console.log(userRoles);
    };

    const checkboxSelection = (value: string): boolean => {
        return userRoles?.length > 0 ? userRoles.includes(value) : false;
    };

    const [delegationUsersList, setDelegationUsersList] = useState<IDelegationUser[]>([]);

    useEffect(() => {
        userStore.getAllUsers().then((response) => {
            setDelegationUsersList(
                userStore.users.map<IDelegationUser>((x) => {
                    return { name: x.name as string, userId: x.userId as number };
                })
            );
        });

        userStore.getCurrentUser().then(resp=>{
            setCurrentUser(resp.user);
            setUserName(resp.user.name);
            setUserTypeSelection(resp.user.userType);
            setUserRoles(resp.user.roles);
            setIsDelegationActive(resp.user.isDelegationActive);
            
            setDelegationEndDate(getDateFormatted(resp.user.delegationEndDate));
            resp.user.delegationUserId ? setSelectedDelegationUser(getSelectedDelegationUser(resp.user.delegationUserId)) : setSelectedDelegationUser(undefined);
            
        });
    }, []);

    const onDelegationUserSearchChange = (event: any, value: any) => {
        console.log(value);
        setIsDelegationUserValid(true);
        setSelectedDelegationUser(value);
    };

    const onDelegationEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDelegationEndDate(e.target.value);
        setIsDelegationEndDateValid(true);
    };

    const onCloseHandle = () => {
        navigate("/viewclaims");
    };

    const onSaveHandle = async (e: any) => {
        if (validate()) {
            let response: any;
            if (currentUser?.userId) {
                const updateUserRequest: IUpdateUserRequest = {
                    userId: currentUser.userId,
                    name: userName as string,
                    isEnabled: userEnabled,
                    userType: userTypeSelection as string,
                    roles: userRoles,
                    isDelegationActive: isDelegationActive as boolean,
                    delegationUserId: Number(selectedDelegationUser?.userId),
                    delegationEndDate: delegationEndDate ? delegationEndDate : null,
                };
                response = await userStore.updateUser(updateUserRequest);

                snackbar.enqueueSnackbar("User updated successfully", {
                    variant: "success",
                });
            }

            userStore.users = [];
        } else {
            console.log("Validation failed");
        }
    };

    const validate = () => {
        let isValid = true;
        if (isDelegationActive) {
            isValid = selectedDelegationUser ? true : false;
            setIsDelegationUserValid(isValid);
            if (!isValid) return false;
            isValid = delegationEndDate ? true : false;
            setIsDelegationEndDateValid(isValid);
            if (!isValid) return false;
        }
        return isValid;
    };

    const delegationOnChangeHandler = (value: boolean) => {
        setIsDelegationActive(value);
        if (value == false) {
            setDelegationEndDate(undefined);
            setSelectedDelegationUser(undefined);
        }
    };

    return (
        <div className="container-fluid p-1">
            <div className="m-4 card">
                <div className="card-header">
                    <div className="row">
                        <div className="col-6 align-content-center">
                            <h1>User Configuration</h1>
                        </div>
                    </div>
                </div>
                {currentUser && (
                    <div className="row card-body w-100 h-100">
                        <form className="needs-validation">
                            <div className="form-group row pb-2">
                                <label htmlFor="name" className="col-md-2 col-form-label">
                                    Name
                                </label>
                                <div className="col-md-4">
                                    <input
                                        type="name"
                                        name="name"
                                        required
                                        value={userName}
                                        disabled={true}
                                        className={isNameValid ? "form-control" : "form-control is-invalid"}
                                        id="inputName"
                                        placeholder="Name"
                                    />
                                </div>
                            </div>
                            <div className="form-group row pb-2">
                                <label htmlFor="userType" className="col-md-2 col-form-label">
                                    User Type
                                </label>
                                <div className="col-md-4">
                                    <input
                                        type="userType"
                                        name="userType"
                                        required
                                        value={userTypeSelection}
                                        disabled={true}
                                        className={"form-control"}
                                        id="inputUserType"
                                        placeholder="User Type"
                                    />
                                </div>
                            </div>
                            <div className="form-group row pb-2">
                                <label htmlFor="userType" className="col-md-2 col-form-label">
                                    User Roles
                                </label>
                                <div className="col-md-4 form-check checkbox-group-padding">
                                    <ul className="list-group">
                                        <li className="list-group-item">
                                            <input
                                                type="checkbox"
                                                disabled={true}
                                                checked={checkboxSelection("Customer Services")}
                                                onChange={onRolesCheck}
                                                value={"Customer Services"}
                                                name="customerservices"
                                            />{" "}
                                            Customer Services
                                        </li>
                                        <li className="list-group-item">
                                            <input
                                                type="checkbox"
                                                disabled={true}
                                                checked={checkboxSelection("Raise Claims")}
                                                onChange={onRolesCheck}
                                                name="raiseclaims"
                                                value={"Raise Claims"}
                                            />{" "}
                                            Raise Claims
                                        </li>
                                        <li className="list-group-item">
                                            <input
                                                type="checkbox"
                                                disabled={true}
                                                checked={checkboxSelection("View Standard Cost")}
                                                onChange={onRolesCheck}
                                                name="viewstandardcost"
                                                value={"View Standard Cost"}
                                            />{" "}
                                            View Standard Cost
                                        </li>
                                        <li className="list-group-item">
                                            <input
                                                type="checkbox"
                                                checked={checkboxSelection("Financial Transaction Manager")}
                                                onChange={onRolesCheck}
                                                disabled={true}
                                                name="financialtransactionmanager"
                                                value={"Financial Transaction Manager"}
                                            />{" "}
                                            Financial Transaction Manager
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="form-group row pb-2">
                                <label htmlFor="enabled" className="col-sm-2 col-form-label">
                                    Delegation Active
                                </label>
                                <div className="col-md-4">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            checked={isDelegationActive ? true : false}
                                            onClick={() => delegationOnChangeHandler(true)}
                                            type="radio"
                                            name="delegationActive"
                                            id="flexRadioDefault1"
                                        />
                                        <label className="form-check-label" htmlFor="flexRadioDefault1">
                                            Yes
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            checked={isDelegationActive ? false : true}
                                            onClick={() => delegationOnChangeHandler(false)}
                                            name="delegationActive"
                                            id="flexRadioDefault2"
                                        />
                                        <label className="form-check-label" htmlFor="flexRadioDefault2">
                                            No
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {isDelegationActive && (
                                <>
                                    <div className="form-group row pb-2">
                                        <label htmlFor="enabled" className="col-sm-2 col-form-label">
                                            Delegate To User
                                        </label>
                                        <div className="col-md-4">
                                            <Autocomplete
                                                disablePortal
                                                title={"Delegation User"}
                                                value={selectedDelegationUser}
                                                onChange={(event, value) => onDelegationUserSearchChange(event, value)}
                                                renderInput={(params) => <TextField {...params} />}
                                                options={delegationUsersList}
                                                getOptionKey={(option: IDelegationUser) => option.userId}
                                                getOptionLabel={(option: IDelegationUser) => option.name}
                                            />
                                            <div className={isDelegationUserValid ? "invalid-feedback" : "invalid-feedback invalid-div-block"}>
                                                Select a user for delegation.
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-group row pb-2">
                                        <label htmlFor="enabled" className="col-sm-2 col-form-label">
                                            Deleg. End Date
                                        </label>
                                        <div className="col-md-4">
                                            <input
                                                required
                                                onChange={onDelegationEndDateChange}
                                                value={delegationEndDate}
                                                type="date"
                                                placeholder="dd/mm/yyyy"
                                                className={isDelegationEndDateValid ? "form-control" : "form-control is-invalid"}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="form-group row button-row-margin">
                                <div className="col-md-12">
                                    <button className="btn btn-primary" onClick={onSaveHandle} type="button">
                                        Save
                                    </button>
                                    <a className="btn btn-primary ms-1" onClick={onCloseHandle}>
                                        Close
                                    </a>
                                    <button type="button" className="btn btn-primary float-end d-inline">
                                        Audit Trail
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
                {!currentUser && <Spinner />}
            </div>
        </div>
    );
});
